/**
 * Parse pasted/typed amount input (Chilean format: dot = thousands, comma = decimal).
 * Used by journal entry Debe/Haber inputs and other amount fields.
 *
 * @param {string} raw - Raw input (e.g. "$1.250", "$1.250,34")
 * @returns {string|null} Normalized string (e.g. "1250", "1250.34"), '' for empty, or null if invalid
 */
export function parseAmountInput(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return '';

  let s = trimmed
    .replace(/\$/g, '')
    .replace(/CLP/gi, '')
    .replace(/\s/g, '');

  const hadComma = s.includes(',');
  s = s.replace(/,/g, '.');
  const parts = s.split('.');

  if (parts.length === 0) return null;

  if (parts.length === 1) {
    const intStr = parts[0].replace(/\D/g, '');
    if (intStr === '') return null;
    const num = parseInt(intStr, 10);
    if (isNaN(num) || num < 0) return null;
    return String(num);
  }

  if (hadComma && parts.length >= 2) {
    const intStr = parts.slice(0, -1).join('').replace(/\D/g, '');
    const decStr = parts[parts.length - 1].replace(/\D/g, '').slice(0, 2);
    const num = parseFloat(intStr + (decStr ? '.' + decStr : ''));
    if (isNaN(num) || num < 0) return null;
    return formatMinimal(num);
  }

  const intStr = parts.join('').replace(/\D/g, '');
  if (intStr === '') return null;
  const num = parseInt(intStr, 10);
  if (isNaN(num) || num < 0) return null;
  return String(num);
}

function formatMinimal(num) {
  if (num % 1 === 0) return String(Math.floor(num));
  const fixed = num.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
}

function digitsOnly(value) {
  return String(value).replace(/[^\d]/g, '');
}

/**
 * Parse an amount typed in search/filter fields.
 * Keeps decimals so FX amounts (USD, EUR, UF, etc.) can be found.
 * Accepts Chilean (1.234,56) and US (1,234.56) formats.
 * A single separator followed by 3 digits is treated as thousands (1.250 → 1250).
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null}
 */
export function parseFilterAmount(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }

  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const s = trimmed.replace(/[$\s]/g, '').replace(/^[+]/, '');
  if (!s || !/\d/.test(s)) return null;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let normalized;

  if (lastComma === -1 && lastDot === -1) {
    normalized = digitsOnly(s);
  } else if (lastComma > lastDot) {
    const integer = digitsOnly(s.slice(0, lastComma));
    const decimals = digitsOnly(s.slice(lastComma + 1)).slice(0, 2);
    normalized = decimals ? `${integer}.${decimals}` : integer;
  } else {
    const afterLast = digitsOnly(s.slice(lastDot + 1));
    const commaCount = (s.match(/,/g) || []).length;
    const isThousandsGroup = afterLast.length === 3 && commaCount === 0;
    if (isThousandsGroup) {
      normalized = digitsOnly(s);
    } else {
      const integer = digitsOnly(s.slice(0, lastDot));
      const decimals = afterLast.slice(0, 2);
      normalized = decimals ? `${integer}.${decimals}` : integer;
    }
  }

  if (!normalized) return null;
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
}

/** True when the text looks like a money amount (digits plus optional separators). */
export function isFilterAmountLike(raw) {
  const compact = String(raw ?? '').trim().replace(/[\s$]/g, '');
  return compact.length > 0 && /^[\d.,]+$/.test(compact);
}

/**
 * True when the user typed an explicit decimal (150,50 / 150.50 / 150.00).
 * Chilean thousands like 1.250 or 1.234.567 are not treated as decimals.
 */
export function looksLikeDecimalFilterAmount(raw) {
  const s = String(raw ?? '').trim().replace(/[\s$]/g, '');
  if (!s) return false;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');

  if (lastComma > lastDot) {
    const after = digitsOnly(s.slice(lastComma + 1));
    return after.length >= 1 && after.length <= 2;
  }

  if (lastDot === -1) return false;
  const after = digitsOnly(s.slice(lastDot + 1));
  if (after.length === 3 && !s.includes(',')) return false;
  return after.length >= 1 && after.length <= 2;
}

/** Strip letters/symbols from a filter amount field while the user types. */
export function sanitizeFilterAmountInput(raw) {
  return String(raw ?? '').replace(/[^\d.,]/g, '');
}
